import React, {useEffect, useState} from 'react';
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip
} from '@mui/material';
import {
  CameraAlt,
  FilterList,
  History,
  LockOutlined,
  Search,
  Storage,
  ThreeSixty
} from '@mui/icons-material';
import {useDispatch, useSelector} from 'react-redux';
import Lottie from 'lottie-react';
import loadingAnimation from '../../../assets/lottie/loading.json';
import api from '../../../api/axios';
import {useIsDesktop} from '../../../hooks/useIsDesktop';
import SkinDbFilter, {defaultFilterState, InventoryFilterState} from './filter/SkinDbFilter';
import {RootState} from '../../../store';
import {closeFilter, openFilter} from '../../../store/slices/sidebarFilterSlice';
import {ItemDTO, OfferDTO} from '../../user/on-sale/types';
import SteamSignIn from '../../navbar/SteamSignIn';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import {RARITY_COLORS, RarityGrade} from '../../utils/RarityColors';
import ItemScreenshotModal from '../../items/ItemScreenshotModal';
import Item3DViewerModal from '../../items/Item3DViewerModal';
import ItemDetailLightModal from '../offer-details/ItemDetailLightModal';
import OwnerHistoryModal from './OwnerHistoryModal';

// --- Types & Interfaces ---

export const wearMap: Record<string, string> = {
  'FACTORY NEW': 'FN',
  'FACTORY_NEW': 'FN',
  'MINIMAL WEAR': 'MW',
  'MINIMAL_WEAR': 'MW',
  'FIELD-TESTED': 'FT',
  'FIELD_TESTED': 'FT',
  'WELL-WORN': 'WW',
  'WELL_WORN': 'WW',
  'BATTLE-SCARRED': 'BS',
  'BATTLE_SCARRED': 'BS'
};

export interface currentOwner {
  hasPremium: number;
  itemFirstSeen: string;
  steamAvatarUrl: string;
  steamProfileName: string;
  steamUrl: string;
}

interface content {
  currentOwner: currentOwner;
  item: ItemDTO;
  offer: OfferDTO;
}

// --- Main Component: SkinDb ---

const SkinDb = () => {
  const dispatch = useDispatch();
  const isDesktop = useIsDesktop();
  const filterCollapsed = useSelector((state: RootState) => state.sidebarFilter.collapsed);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const {rate, symbol} = useSelector((state: RootState) => state.currency);

  const [filters, setFilters] = useState<InventoryFilterState>(defaultFilterState);
  const [items, setItems] = useState<content[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalSkins, setTotalSkins] = useState<number | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  // Modal States
  const [screenshotModal, setScreenshotModal] = useState<{ open: boolean; assetId: string }>({
    open: false,
    assetId: '',
  });

  const [viewer3DModal, setViewer3DModal] = useState<{ open: boolean; assetId: string }>({
    open: false,
    assetId: '',
  });

  const [historyModal, setHistoryModal] = useState<{ open: boolean; assetId: string | null }>({
    open: false,
    assetId: null,
  });

  const [lightModalItem, setLightModalItem] = useState<ItemDTO | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      if (isDesktop) {
        dispatch(openFilter());
      } else {
        dispatch(closeFilter());
      }
    }
  }, [isDesktop, dispatch, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      api.get('/item/total-amount')
      .then(res => setTotalSkins(res.data))
      .catch(err => console.error('Error fetching total amount', err));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  const fetchSkins = async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const response = await api.get(`/item/records`, {
        params: {
          ...filters,
          page,
          size: rowsPerPage
        }
      });
      setItems(response.data.content);
      setTotalCount(response.data.totalElements);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkins();
  }, [page, rowsPerPage, filters, isAuthenticated]);

  const convertFromUSD = (usd: number) => usd * rate

  const handleOpenScreenshot = (assetId: string) => {
    setScreenshotModal({ open: true, assetId });
  };

  const handleCloseScreenshot = () => {
    setScreenshotModal(prev => ({ ...prev, open: false }));
  };

  const handleOpen3DViewer = (assetId: string) => {
    setViewer3DModal({ open: true, assetId });
  };

  const handleClose3DViewer = () => {
    setViewer3DModal(prev => ({ ...prev, open: false }));
  };

  const renderAttachments = (item: ItemDTO) => {
    const attachments = item.attachments || [];
    if (attachments.length === 0) return null;

    return (
        <div className="flex flex-row flex-nowrap">
          {attachments.map((att, idx) => (
              <Tooltip key={att.id || idx} title={att.marketHashName} arrow>
                <div className="p-0.5 transition-all duration-200 shrink-0">
                  <img
                      src={att.imageLink || ''}
                      alt={att.marketHashName || 'attachment'}
                      className="w-8 h-8 object-contain drop-shadow-sm transition-transform duration-200 hover:scale-[2.0] cursor-pointer"
                  />
                </div>
              </Tooltip>
          ))}
        </div>
    );
  };

  if (!isAuthenticated) {
    return (
        <Box className="flex flex-col items-center justify-center min-h-[80vh] px-4">
          <div className="max-w-md w-full text-center p-10 bg-white dark:bg-neutral-900/40 border border-gray-100 dark:border-white/5 shadow-xl">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 mb-6">
              <LockOutlined className="text-blue-500" sx={{fontSize: 32}}/>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-3 uppercase tracking-tighter">
              Entdecke {totalSkins ? totalSkins.toLocaleString() : 'unsere'} Skins
            </h2>

            <p className="text-gray-500 dark:text-gray-400 mb-8 text-sm leading-relaxed">
              Wir tracken aktuell <strong>{totalSkins ? totalSkins.toLocaleString() : 'viele'} Skins</strong> mit detaillierten Informationen und vollständiger
              User-History.
              Melde dich an, um Zugriff auf die gesamte Datenbank zu erhalten.
            </p>

            <div className="flex justify-center scale-110">
              <SteamSignIn/>
            </div>
          </div>
        </Box>
    );
  }

  const headerCellStyle = {
    border: 'none',
    backgroundColor: 'white',
    '.dark &': {
      backgroundColor: '#111111',
    },
    zIndex: 50,
  };

  return (
      <Box className="px-3 md:p-4 mt-20 flex gap-4 h-[calc(100vh-100px)]">

        <ItemScreenshotModal
            open={screenshotModal.open}
            assetId={screenshotModal.assetId}
            onClose={handleCloseScreenshot}
        />

        <Item3DViewerModal
            open={viewer3DModal.open}
            assetId={viewer3DModal.assetId}
            onClose={handleClose3DViewer}
        />

        <OwnerHistoryModal
            open={historyModal.open}
            assetId={historyModal.assetId}
            onClose={() => setHistoryModal({ open: false, assetId: null })}
        />

        <ItemDetailLightModal
            open={!!lightModalItem}
            onClose={() => setLightModalItem(null)}
            item={lightModalItem}
        />

        {!filterCollapsed && !isDesktop && (
            <div className="lg:hidden fixed left-0 right-0 bottom-0 top-[65px] overflow-y-auto z-50 bg-white dark:bg-neutral-950 flex justify-center">
              <div className="w-full max-w-md">
                <SkinDbFilter
                    filters={filters}
                    onChange={(newFilters) => setFilters(newFilters)}
                    onClose={() => dispatch(closeFilter())}
                    collapsed={false}
                />
              </div>
            </div>
        )}

        <div
            className={`hidden lg:block overflow-hidden transition-all duration-300 ease-in-out ${
                filterCollapsed ? 'w-0 opacity-0' : 'w-72 opacity-100'
            }`}
        >
          <SkinDbFilter
              filters={filters}
              onChange={(newFilters) => setFilters(newFilters)}
              collapsed={false}
              onClose={() => dispatch(closeFilter())}
          />
        </div>

        <div className="flex-1 min-w-0 flex flex-col bg-white dark:bg-neutral-900/40 border border-gray-100 dark:border-white/5 md:p-4 p-2 overflow-hidden">

          <div className="flex flex-col xl:flex-row justify-between items-center gap-4 mb-4 shrink-0">
            <div className="flex items-center gap-2 self-start xl:self-center">
              <Storage className="text-dark-primary"/>
              <h2 className="text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tighter">
                Skin Database
              </h2>
            </div>

            <div className="flex flex-1 gap-2 items-center w-full md:max-w-md justify-end">
              <TextField
                  fullWidth
                  size="small"
                  placeholder="Search Skin Name..."
                  variant="outlined"
                  value={filters.name}
                  onChange={(e) => setFilters({...filters, name: e.target.value})}
                  className="bg-gray-50 dark:bg-neutral-800/50"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {borderColor: 'rgba(0,0,0,0.05)', borderRadius: 0},
                    '& .MuiInputBase-input': {fontSize: '11px'}
                  }}
                  InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                          <Search className="text-neutral-500" sx={{fontSize: 18}}/>
                        </InputAdornment>
                    ),
                  }}
              />
            </div>
          </div>

          <Divider className="!border-gray-100 dark:!border-white/5 shrink-0"/>

          {loading && items.length === 0 ? (
              <div className="flex-1 flex justify-center items-center">
                <Lottie animationData={loadingAnimation} loop className="w-96"/>
              </div>
          ) : items.length > 0 ? (
              <TableContainer
                  component={Paper}
                  className="flex-1 bg-transparent shadow-none border-none mt-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-neutral-700"
                  sx={{borderRadius: 0}}
              >
                <Table size="small" stickyHeader className="border-separate border-spacing-y-2">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellStyle} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Rank</TableCell>
                      <TableCell sx={headerCellStyle} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Item</TableCell>
                      <TableCell sx={headerCellStyle} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Float</TableCell>
                      <TableCell sx={headerCellStyle} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Seed</TableCell>
                      <TableCell sx={headerCellStyle} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Attachments</TableCell>
                      <TableCell sx={headerCellStyle} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Offer</TableCell>
                      <TableCell align="right" sx={headerCellStyle}
                                 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((skin, index) => {
                      const {catalog, details} = skin.item;
                      const offer = skin.offer;
                      const rarityColor = RARITY_COLORS[details?.rarity as RarityGrade] ?? '#ffffff';
                      const normalizedWear = catalog?.wearName?.toUpperCase() || '';
                      const shortWear = wearMap[normalizedWear] || catalog?.wearName || '';

                      return (
                          <TableRow
                              key={skin.item.id}
                              className="group bg-gray-50/30 dark:bg-neutral-900/20 hover:bg-gray-100/50 dark:hover:bg-white/[0.03] transition-all duration-200"
                          >
                            <TableCell sx={{border: 'none'}} className="text-xs font-mono text-gray-400">
                              #{(page * rowsPerPage) + index + 1}
                            </TableCell>
                            <TableCell sx={{border: 'none'}}>
                              <div
                                  onClick={() => setLightModalItem(skin.item)}
                                  className="flex items-center gap-4 cursor-pointer group/item"
                              >
                                <div className="relative flex items-center shrink-0">
                                  <div style={{width: 3, height: 42, background: rarityColor}} className="rounded-full"/>
                                  <div className="relative ml-1 flex items-center justify-center">
                                    <img
                                        src={catalog?.imageLink ?? ''}
                                        referrerPolicy="no-referrer"
                                        className="w-12 h-12 object-contain drop-shadow-md z-10 transition-transform duration-200 group-hover/item:scale-105"
                                        alt=""
                                    />
                                    <div style={{
                                      position: 'absolute',
                                      top: '50%',
                                      left: -4,
                                      transform: 'translateY(-50%)',
                                      width: 48,
                                      height: 42,
                                      background: `linear-gradient(to right, ${rarityColor}33, transparent)`,
                                      zIndex: 1
                                    }}/>
                                  </div>
                                </div>
                                <div className="flex flex-col">
                                  <p className="text-sm font-bold text-gray-800 dark:text-gray-100 group-hover/item:text-dark-primary transition-colors leading-tight">
                                    {catalog.groupName}
                                  </p>
                                  <div className="flex gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                                    {catalog?.isStattrak && <span className="text-orange-500 font-bold">ST</span>}
                                    {catalog?.isSouvenir && <span className="text-yellow-600 font-bold">SN</span>}
                                    {shortWear && <span>{shortWear}</span>}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell sx={{border: 'none'}}>
                            <span className="text-[11px] font-mono text-gray-600 dark:text-gray-300">
                              {details?.floatValue?.toFixed(15) ?? ''}
                            </span>
                            </TableCell>
                            <TableCell sx={{border: 'none'}}>
                            <span className="text-[11px] font-mono text-gray-600 dark:text-gray-300">
                              #{details?.paintSeed ?? ''}
                            </span>
                            </TableCell>
                            <TableCell sx={{border: 'none'}}>
                              {renderAttachments(skin.item)}
                            </TableCell>
                            <TableCell sx={{border: 'none'}}>
                              {offer?.priceMinor ? (
                                  <a
                                      href={`/market/offer/${offer.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex flex-col items-start hover:opacity-80 transition-opacity text-left no-underline"
                                  >
                                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter leading-none mb-0.5">Show Offer</span>
                                    <span className="font-mono font-black text-emerald-500 text-xs">
                                    {symbol + convertFromUSD(offer.priceMinor / 100).toFixed(2)}
                                  </span>
                                  </a>
                              ) : (
                                  <span className="text-[10px] font-bold text-gray-300 dark:text-neutral-700 uppercase italic"></span>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{border: 'none'}}>
                              <div className="flex items-center justify-end gap-1">
                                {skin.currentOwner.steamAvatarUrl ? (
                                    <Tooltip title={skin.currentOwner.steamProfileName || 'View Steam Profile'} arrow>
                                      <a
                                          href={skin.currentOwner.steamUrl + '/inventory/'}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="w-8 h-8 bg-neutral-800/10 rounded mr-2 flex items-center justify-center overflow-hidden hover:opacity-80 transition-opacity"
                                      >
                                        <img
                                            src={skin.currentOwner.steamAvatarUrl}
                                            alt="owner avatar"
                                            className="w-full h-full rounded-sm object-cover"
                                        />
                                      </a>
                                    </Tooltip>
                                ) : (
                                    <div className="w-8 h-8 bg-neutral-800/10 rounded mr-2 flex items-center justify-center overflow-hidden">
                                      <Tooltip title="Premium Required" arrow>
                                        <HelpCenterIcon className="text-neutral-500" sx={{fontSize: 18, cursor: 'help'}}/>
                                      </Tooltip>
                                    </div>
                                )}

                                <Tooltip title="Inspect In-Game" arrow>
                                  <IconButton
                                      size="small"
                                      className="text-gray-400 hover:text-blue-500 transition-colors"
                                      onClick={() => {
                                        if (details?.ingameLink) window.location.href = details.ingameLink;
                                      }}
                                  >
                                    <ZoomInIcon sx={{fontSize: 24}}/>
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="View Screenshot" arrow>
                                  <IconButton
                                      size="small"
                                      className="text-gray-400 hover:text-blue-500 transition-colors"
                                      onClick={() => handleOpenScreenshot(skin.item.itemAssetId)}
                                  >
                                    <CameraAlt sx={{fontSize: 24}}/>
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="360° 3D Viewer" arrow>
                                  <IconButton
                                      size="small"
                                      className="text-gray-400 hover:text-blue-500 transition-colors"
                                      onClick={() => handleOpen3DViewer(skin.item.itemAssetId)}
                                  >
                                    <ThreeSixty sx={{fontSize: 24}}/>
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Show History" arrow>
                                  <IconButton
                                      size="small"
                                      className="text-gray-400 hover:text-blue-500 transition-colors"
                                      onClick={() => setHistoryModal({ open: true, assetId: skin.item.itemAssetId })}
                                  >
                                    <History sx={{fontSize: 24}}/>
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
          ) : (
              <div className="flex-1 flex flex-col items-center justify-center opacity-40">
                <FilterList sx={{fontSize: 80}} className="text-gray-300 mb-2"/>
                <p className="text-sm font-bold text-gray-400 uppercase tracking-widest italic text-center">
                  No skins found in database
                </p>
              </div>
          )}

          <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-100 dark:border-white/5 mt-auto shrink-0">
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-gray-400 font-bold uppercase">Rows</span>
              <span className="text-[10px] text-gray-400 font-mono">{totalCount} skins tracked</span>
            </div>
            <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                sx={{
                  border: 'none',
                  '& .MuiTablePagination-actions': {
                    '& .MuiButtonBase-root': {
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      color: '#3b82f6',
                      borderRadius: 0,
                      margin: '0 4px'
                    }
                  }
                }}
            />
          </div>
        </div>
      </Box>
  );
};

export default SkinDb;
